import { useState } from "react";
import { GraduationCap, ArrowUp, AlertCircle, CheckCircle2, Loader2, ListChecks } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { ACADEMIC_YEARS } from "../lib/constants";
import { promotePreview, promoteExecute } from "../lib/api";

export default function Promotion() {
  const [year, setYear] = useState("all");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    setPreview(null);
    try {
      const data = await promotePreview(year === "all" ? null : year);
      setPreview(data);
      if (data.total === 0) toast.info("لا يوجد طلاب مطابقون للمعايير");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التحميل");
    } finally {
      setLoading(false);
    }
  };

  const execute = async () => {
    setExecuting(true);
    try {
      const data = await promoteExecute(year === "all" ? null : year);
      toast.success(`تم ترقية ${data.promoted} طالب، وتخريج ${data.graduated} طالب`);
      setConfirmOpen(false);
      setPreview(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "تعذر التنفيذ");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <div className="max-w-4xl">
      <PageHeader title="الترقية الجماعية لنهاية العام" subtitle="نقل جميع الطلاب إلى الصف التالي وتخريج طلاب الثالث الثانوي" icon={GraduationCap} />

      <Card className="p-5 sm:p-7 border-slate-200 mb-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
          <div className="space-y-1.5 md:col-span-2">
            <Label>العام الدراسي للترقية منه</Label>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="h-11" dir="rtl" data-testid="promo-year"><SelectValue /></SelectTrigger>
              <SelectContent dir="rtl">
                <SelectItem value="all">جميع الطلاب الحاليين</SelectItem>
                {ACADEMIC_YEARS.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-500 leading-relaxed">
              عند الترقية: الصف الأول → الثاني، الثاني → الثالث ... الثالث الثانوي → خريجون.
              يتم تحديث الصف والمرحلة والعام الدراسي تلقائياً مع الإبقاء على نفس رقم الفصل.
            </p>
          </div>
          <Button onClick={loadPreview} disabled={loading} className="h-11 bg-teal-700 hover:bg-teal-800 text-white gap-2 btn-elevate" data-testid="btn-preview">
            {loading ? <Loader2 className="animate-spin" size={16} /> : <ListChecks size={16} />}
            معاينة الترقية
          </Button>
        </div>
      </Card>

      {preview && (
        <Card className="p-5 border-slate-200 mb-5" data-testid="promo-preview">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-5">
            <div className="rounded-xl bg-teal-50 border border-teal-200 p-4">
              <div className="flex items-center gap-2 text-teal-700 mb-1"><ArrowUp size={18} /> <span className="font-cairo font-bold">سيُرقّى</span></div>
              <div className="text-3xl font-extrabold text-teal-800 tabular-nums">{preview.promoted}</div>
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4">
              <div className="flex items-center gap-2 text-amber-700 mb-1"><GraduationCap size={18} /> <span className="font-cairo font-bold">سيتخرج</span></div>
              <div className="text-3xl font-extrabold text-amber-800 tabular-nums">{preview.graduated}</div>
            </div>
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 col-span-2 sm:col-span-1">
              <div className="flex items-center gap-2 text-slate-700 mb-1"><CheckCircle2 size={18} /> <span className="font-cairo font-bold">الإجمالي</span></div>
              <div className="text-3xl font-extrabold text-slate-800 tabular-nums">{preview.total}</div>
            </div>
          </div>

          {preview.by_grade?.length > 0 && (
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-teal-700 text-white">
                  <tr>
                    <th className="px-3 py-2 text-right font-cairo">الصف الحالي</th>
                    <th className="px-3 py-2 text-center font-cairo">العدد</th>
                    <th className="px-3 py-2 text-right font-cairo">الصف التالي</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.by_grade.map((g, i) => (
                    <tr key={i} className="odd:bg-white even:bg-slate-50/60">
                      <td className="px-3 py-2 text-slate-800">{g.grade}</td>
                      <td className="px-3 py-2 text-center text-slate-700 tabular-nums">{g.count}</td>
                      <td className="px-3 py-2 text-teal-700 font-medium">{g.next || "🎓 خريج"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {preview.total > 0 && (
            <div className="mt-5 flex justify-end">
              <Button onClick={() => setConfirmOpen(true)} className="bg-rose-600 hover:bg-rose-700 text-white gap-2 h-11 px-6 btn-elevate" data-testid="btn-confirm-promo">
                <ArrowUp size={16} /> تنفيذ الترقية الآن
              </Button>
            </div>
          )}
        </Card>
      )}

      <Card className="p-4 border-amber-200 bg-amber-50/50">
        <div className="flex items-start gap-3 text-sm text-amber-800">
          <AlertCircle className="text-amber-600 shrink-0" size={20} />
          <div>
            <div className="font-cairo font-bold mb-1">تنبيه مهم</div>
            <p className="leading-relaxed text-amber-700">
              عملية الترقية لا يمكن التراجع عنها تلقائياً. ينصح بأخذ نسخة احتياطية من البيانات قبل التنفيذ،
              والقيام بها مرة واحدة في نهاية كل عام دراسي.
            </p>
          </div>
        </div>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent dir="rtl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-cairo">تأكيد الترقية الجماعية</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم ترقية <strong>{preview?.promoted}</strong> طالب وتخريج <strong>{preview?.graduated}</strong> طالب.
              هل أنت متأكد من المتابعة؟
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={executing}>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={execute} disabled={executing} className="bg-rose-600 hover:bg-rose-700 gap-2">
              {executing ? <Loader2 className="animate-spin" size={16} /> : <ArrowUp size={16} />}
              {executing ? "جاري التنفيذ..." : "تأكيد"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
